/**
 * @desc The Workflows list's URL filters (H2-7, task 8.5), apart from the rest of
 * `lib/automations.ts`: `parseAutomationsSearch` is the route's `validateSearch`, which stays in
 * the entry chunk (docs/code-splitting.md), and `lib/automations.ts` reads the automations model
 * namespaces at import, which would put their Zod schemas on every first load. Keep this module
 * free of imports; `lib/automations.ts` re-exports it.
 */

export const workflowStatusFilters = ['draft', 'ready', 'archived'] as const;
export type WorkflowStatusFilter = (typeof workflowStatusFilters)[number];

/** @desc The Workflows list's URL state. Defaults (all live statuses, either state) are omitted. */
export type WorkflowsSearch = {
  status?: WorkflowStatusFilter;
  enabled?: boolean;
};

const isStatusFilter = (value: unknown): value is WorkflowStatusFilter =>
  typeof value === 'string' && (workflowStatusFilters as readonly string[]).includes(value);

/** @desc `validateSearch` for `/admin/ario/workflows`: unknown values are dropped. */
export const parseAutomationsSearch = (raw: Record<string, unknown>): WorkflowsSearch => {
  const search: WorkflowsSearch = {};
  if (isStatusFilter(raw.status)) search.status = raw.status;
  if (raw.enabled === true || raw.enabled === 'true') search.enabled = true;
  if (raw.enabled === false || raw.enabled === 'false') search.enabled = false;
  return search;
};

/** @desc A patch applied to the search, normalised so a cleared filter leaves the URL. */
export const withAutomationsSearch = (
  current: WorkflowsSearch,
  patch: { status?: WorkflowStatusFilter | null; enabled?: boolean | null },
): WorkflowsSearch => {
  const merged: Record<string, unknown> = { ...current, ...patch };
  return parseAutomationsSearch(merged);
};

/** @desc The `automations.workflow.list` input a search implies. */
export const workflowListInput = (search: WorkflowsSearch) => ({
  limit: 100,
  ...(search.status ? { status: search.status } : {}),
  ...(search.status === 'archived' ? { includeArchived: true } : {}),
});

/** @desc The `enabled` filter is client-side: the list procedure filters by status only. */
export const matchesEnabled = (workflow: { enabled: boolean }, search: WorkflowsSearch): boolean =>
  search.enabled === undefined || workflow.enabled === search.enabled;
